/**
 * The rules of the floor plan editor (prototype, Beheer › Tafelplan), as pure functions: items never
 * overlap, a dragged item slides along the free axis, new items go to the first free spot, round
 * tables stay round, tables need at least 30 and walls 8.
 */
import type { FloorItem, FloorRoom } from "../../api/types";

export const WIDTH = 1000;
export const HEIGHT = 640;
export const SNAP = 10;
export const MIN_SIZE = 30;
export const MIN_WALL = 8;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Whether `box` (the new place of `item`) touches another item of the room. */
export function hits(room: FloorRoom, item: FloorItem, box: Box): boolean {
  return room.items.some(
    (other) =>
      other.id !== item.id &&
      box.x < other.x + other.w &&
      box.x + box.w > other.x &&
      box.y < other.y + other.h &&
      box.y + box.h > other.y,
  );
}

export const snapTo = (value: number, snap: boolean) => (snap ? Math.round(value / SNAP) * SNAP : Math.round(value));

/** The smallest height (or width) of an item: a wall can be thin. */
export const minThin = (item: FloorItem) => (item.kind === "wall" ? MIN_WALL : MIN_SIZE);

/**
 * Dragging: the item follows the pointer; where it would touch another item it slides along the
 * free axis, otherwise it stays (prototype). Returns the new place and whether it was blocked.
 */
export function moveTo(room: FloorRoom, item: FloorItem, x: number, y: number): { box: Box; hit: boolean } {
  const wanted = {
    x: Math.max(0, Math.min(WIDTH - item.w, x)),
    y: Math.max(0, Math.min(HEIGHT - item.h, y)),
    w: item.w,
    h: item.h,
  };
  if (!hits(room, item, wanted)) return { box: wanted, hit: false };
  const alongX = { ...wanted, y: item.y };
  if (!hits(room, item, alongX)) return { box: alongX, hit: true };
  const alongY = { ...wanted, x: item.x };
  if (!hits(room, item, alongY)) return { box: alongY, hit: true };
  return { box: { x: item.x, y: item.y, w: item.w, h: item.h }, hit: true };
}

/** Resizing with the corner: within the plan, at least the minimum, round stays round. */
export function resizeTo(room: FloorRoom, item: FloorItem, w: number, h: number): { box: Box; hit: boolean } {
  let width = Math.max(MIN_SIZE, Math.min(WIDTH - item.x, w));
  let height = Math.max(minThin(item), Math.min(HEIGHT - item.y, h));
  if (item.shape === "round") width = height = Math.min(Math.max(width, height), WIDTH - item.x, HEIGHT - item.y);
  const wanted = { x: item.x, y: item.y, w: width, h: height };
  if (!hits(room, item, wanted)) return { box: wanted, hit: false };
  if (item.shape !== "round") {
    const onlyW = { ...wanted, h: item.h };
    if (!hits(room, item, onlyW)) return { box: onlyW, hit: true };
    const onlyH = { ...wanted, w: item.w };
    if (!hits(room, item, onlyH)) return { box: onlyH, hit: true };
  }
  return { box: { x: item.x, y: item.y, w: item.w, h: item.h }, hit: true };
}

/** The first free spot for something of this size, 10 away from everything (prototype). */
export function freeSpot(room: FloorRoom, w: number, h: number): { x: number; y: number } {
  for (let y = 40; y <= HEIGHT - h - 20; y += 20) {
    for (let x = 40; x <= WIDTH - w - 20; x += 20) {
      const free = !room.items.some(
        (other) =>
          x < other.x + other.w + 10 && x + w + 10 > other.x && y < other.y + other.h + 10 && y + h + 10 > other.y,
      );
      if (free) return { x, y };
    }
  }
  return { x: 40, y: 40 };
}

/** All table numbers of the shop, whatever the room. */
export const tableLabels = (rooms: FloorRoom[]) =>
  rooms.flatMap((room) => room.items).flatMap((item) => (item.kind === "table" && item.label ? [item.label] : []));

/** The lowest free number for a new table ("13" when 1–12 exist). */
export function nextLabel(rooms: FloorRoom[]): string {
  const used = new Set(tableLabels(rooms).map((label) => label.toUpperCase()));
  let number = 1;
  while (used.has(String(number))) number++;
  return String(number);
}

/** A random id, also where crypto.randomUUID is missing (a till opened over plain http). */
export function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** What the toolbar adds (prototype sizes): tables with the next number, decoration with a text. */
export function newItem(
  rooms: FloorRoom[],
  room: FloorRoom,
  kind: "rect" | "round" | "bar" | "wall" | "text",
): FloorItem {
  if (kind === "rect" || kind === "round") {
    const size = kind === "round" ? { w: 110, h: 110 } : { w: 140, h: 100 };
    return {
      id: newId(),
      kind: "table",
      label: nextLabel(rooms),
      ...freeSpot(room, size.w, size.h),
      ...size,
      shape: kind,
      seats: kind === "round" ? 2 : 4,
      text: null,
    };
  }
  const size = { bar: { w: 400, h: 50 }, wall: { w: 300, h: 14 }, text: { w: 180, h: 30 } }[kind];
  return {
    id: newId(),
    kind,
    label: null,
    ...freeSpot(room, size.w, size.h),
    ...size,
    shape: "rect",
    seats: null,
    text: kind === "bar" ? "Toog" : kind === "text" ? "Tekst" : "",
  };
}

/** A room name nobody uses yet: "Ruimte 3". */
export function newRoomName(rooms: FloorRoom[]): string {
  let number = rooms.length + 1;
  while (rooms.some((room) => room.name.toLowerCase() === `ruimte ${String(number)}`)) number++;
  return `Ruimte ${String(number)}`;
}

/** The plan with one room replaced. */
export const withRoom = (rooms: FloorRoom[], room: FloorRoom) =>
  rooms.map((item) => (item.id === room.id ? room : item));

/** The room with one item replaced (or removed with null). */
export const withItem = (room: FloorRoom, id: string, item: FloorItem | null): FloorRoom => ({
  ...room,
  items:
    item === null
      ? room.items.filter((other) => other.id !== id)
      : room.items.map((other) => (other.id === id ? item : other)),
});
